import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    Divider,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    HStack,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Switch,
    Text,
    Textarea,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import SchoolSearchSelect from "./SchoolSearchSelect";
import {
    computeSaleTotals,
    formatCurrency,
    PAYMENT_MODES,
    PAYMENT_STATUSES,
    VAT_RATE,
} from "./Services";

// Default product for now — the only thing we sell through this screen.
const DEFAULT_PRODUCT = "Digital Ghanti";

const todayYmd = () => new Date().toISOString().slice(0, 10);

const initialSale = {
    product: DEFAULT_PRODUCT,
    transactionDate: todayYmd(),
    amount: "",
    discount: "",
    vatApplicable: false,
    paymentStatus: "PENDING",
    paymentMode: "CASH",
    notes: "",
};

function AddSaleModal({isOpen, onClose, onSubmit}) {
    const toast = useToast();
    const labelColor = useColorModeValue("gray.600", "gray.300");
    const summaryBg = useColorModeValue("gray.50", "whiteAlpha.100");
    const summaryBorder = useColorModeValue("gray.100", "whiteAlpha.200");

    const [school, setSchool] = useState(null);
    const [form, setForm] = useState(initialSale);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const totals = useMemo(() => computeSaleTotals(form), [form]);

    const setField = (name, value) => {
        setForm((current) => ({...current, [name]: value}));
        setErrors((current) => ({...current, [name]: undefined}));
    };

    const validate = () => {
        const next = {};
        if (!school?.id) next.school = "Please select a school.";
        if (!form.transactionDate) next.transactionDate = "Transaction date is required.";
        if (form.amount === "" || Number(form.amount) <= 0) next.amount = "Enter a valid amount.";
        if (form.discount !== "" && Number(form.discount) < 0) next.discount = "Discount cannot be negative.";
        if (Number(form.discount || 0) > Number(form.amount || 0)) next.discount = "Discount cannot exceed amount.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const resetAndClose = () => {
        setForm(initialSale);
        setSchool(null);
        setErrors({});
        onClose();
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSubmitting(true);

        // Shape mirrors what the backend will eventually accept/return.
        const payload = {
            schoolId: school.id,
            schoolName: school.name,
            product: form.product,
            transactionDate: form.transactionDate,
            amount: Number(form.amount) || 0,
            discount: Number(form.discount) || 0,
            vatApplicable: form.vatApplicable,
            vatAmount: totals.vatAmount,
            subtotal: totals.subtotal,
            totalAmount: totals.total,
            paymentStatus: form.paymentStatus,
            paymentMode: form.paymentMode,
            notes: form.notes,
        };

        try {
            await onSubmit(payload);
            toast({
                title: "Sale recorded",
                description: `${form.product} sale for ${school.name} added.`,
                status: "success",
                duration: 4000,
                isClosable: true,
            });
            resetAndClose();
        } catch (e) {
            console.error("Error while saving sale e:", e);
            toast({
                title: "Error",
                description: "Failed to save the sale. Please try again.",
                status: "error",
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={resetAndClose} isCentered size="2xl" scrollBehavior="inside">
            <ModalOverlay/>
            <ModalContent borderRadius="16px">
                <ModalHeader>Add Sale</ModalHeader>
                <ModalCloseButton/>
                <ModalBody>
                    <Flex direction="column" gap="16px">
                        {/* School + product */}
                        <SimpleGrid columns={{base: 1, md: 2}} spacing="14px">
                            <FormControl isRequired isInvalid={!!errors.school}>
                                <FormLabel fontSize="sm" color={labelColor}>School</FormLabel>
                                <SchoolSearchSelect
                                    selectedSchool={school}
                                    onSelect={(s) => {
                                        setSchool(s);
                                        setErrors((c) => ({...c, school: undefined}));
                                    }}
                                    isInvalid={!!errors.school}
                                />
                                <FormErrorMessage>{errors.school}</FormErrorMessage>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm" color={labelColor}>Product</FormLabel>
                                <Select
                                    value={form.product}
                                    onChange={(e) => setField("product", e.target.value)}
                                    borderRadius="8px"
                                >
                                    <option value="Digital Ghanti">Digital Ghanti</option>
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        {/* Transaction date + amount + discount */}
                        <SimpleGrid columns={{base: 1, md: 3}} spacing="14px">
                            <FormControl isRequired isInvalid={!!errors.transactionDate}>
                                <FormLabel fontSize="sm" color={labelColor}>Transaction Date</FormLabel>
                                <Input
                                    type="date"
                                    value={form.transactionDate}
                                    onChange={(e) => setField("transactionDate", e.target.value)}
                                    borderRadius="8px"
                                />
                                <FormErrorMessage>{errors.transactionDate}</FormErrorMessage>
                            </FormControl>

                            <FormControl isRequired isInvalid={!!errors.amount}>
                                <FormLabel fontSize="sm" color={labelColor}>Amount</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" color="gray.400" fontSize="sm" children="Rs."/>
                                    <Input
                                        type="number"
                                        min="0"
                                        pl="36px"
                                        placeholder="0.00"
                                        value={form.amount}
                                        onChange={(e) => setField("amount", e.target.value)}
                                        borderRadius="8px"
                                    />
                                </InputGroup>
                                <FormErrorMessage>{errors.amount}</FormErrorMessage>
                            </FormControl>

                            <FormControl isInvalid={!!errors.discount}>
                                <FormLabel fontSize="sm" color={labelColor}>Discount</FormLabel>
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none" color="gray.400" fontSize="sm" children="Rs."/>
                                    <Input
                                        type="number"
                                        min="0"
                                        pl="36px"
                                        placeholder="0.00"
                                        value={form.discount}
                                        onChange={(e) => setField("discount", e.target.value)}
                                        borderRadius="8px"
                                    />
                                </InputGroup>
                                <FormErrorMessage>{errors.discount}</FormErrorMessage>
                            </FormControl>
                        </SimpleGrid>

                        {/* VAT toggle */}
                        <FormControl display="flex" alignItems="center" gap="12px">
                            <Switch
                                isChecked={form.vatApplicable}
                                onChange={(e) => setField("vatApplicable", e.target.checked)}
                                colorScheme="blue"
                            />
                            <FormLabel fontSize="sm" color={labelColor} mb="0">
                                VAT applicable ({Math.round(VAT_RATE * 100)}%)
                            </FormLabel>
                        </FormControl>

                        {/* Money summary */}
                        <Box bg={summaryBg} border="1px solid" borderColor={summaryBorder} borderRadius="12px" p="12px 16px">
                            <Flex justify="space-between" py="2px">
                                <Text fontSize="sm" color={labelColor}>Subtotal</Text>
                                <Text fontSize="sm">{formatCurrency(totals.subtotal)}</Text>
                            </Flex>
                            <Flex justify="space-between" py="2px">
                                <Text fontSize="sm" color={labelColor}>
                                    VAT {form.vatApplicable ? `(${Math.round(VAT_RATE * 100)}%)` : "(N/A)"}
                                </Text>
                                <Text fontSize="sm">{formatCurrency(totals.vatAmount)}</Text>
                            </Flex>
                            <Divider my="6px"/>
                            <Flex justify="space-between" py="2px">
                                <Text fontSize="md" fontWeight="bold">Total Amount</Text>
                                <Text fontSize="md" fontWeight="bold" color="blue.400">
                                    {formatCurrency(totals.total)}
                                </Text>
                            </Flex>
                        </Box>

                        {/* Payment status + mode */}
                        <SimpleGrid columns={{base: 1, md: 2}} spacing="14px">
                            <FormControl>
                                <FormLabel fontSize="sm" color={labelColor}>Payment Status</FormLabel>
                                <Select
                                    value={form.paymentStatus}
                                    onChange={(e) => setField("paymentStatus", e.target.value)}
                                    borderRadius="8px"
                                >
                                    {PAYMENT_STATUSES.map((s) => (
                                        <option key={s.value} value={s.value}>{s.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm" color={labelColor}>Payment Mode</FormLabel>
                                <Select
                                    value={form.paymentMode}
                                    onChange={(e) => setField("paymentMode", e.target.value)}
                                    borderRadius="8px"
                                >
                                    {PAYMENT_MODES.map((m) => (
                                        <option key={m.value} value={m.value}>{m.label}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </SimpleGrid>

                        {/* Key notes */}
                        <FormControl>
                            <FormLabel fontSize="sm" color={labelColor}>Key Notes</FormLabel>
                            <Textarea
                                value={form.notes}
                                onChange={(e) => setField("notes", e.target.value)}
                                placeholder="Reference no., remarks, follow-up details…"
                                rows={3}
                                borderRadius="8px"
                            />
                        </FormControl>
                    </Flex>
                </ModalBody>
                <ModalFooter>
                    <HStack spacing="10px">
                        <Button variant="ghost" onClick={resetAndClose}>Cancel</Button>
                        <Button colorScheme="blue" onClick={handleSubmit} isLoading={submitting}>
                            Save Sale
                        </Button>
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default AddSaleModal;
